import { Box, Divider, FormLayoutGroup, NumberField, Stack, Strong, Text } from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import type { AutoRechargeErrors, AutoRechargeSettings } from '../utils/addon-purchase';
import { DollarField, ToggleRow } from './top-up-fields';
import * as styles from '../styles.css';

type AutoRechargeBoxProps = {
  settings: AutoRechargeSettings;
  errors: AutoRechargeErrors;
  rechargeCents: number;
  onChange: (settings: AutoRechargeSettings) => void;
};

export function AutoRechargeBox({ settings, errors, rechargeCents, onChange }: AutoRechargeBoxProps) {
  const update = (patch: Partial<AutoRechargeSettings>) => onChange({ ...settings, ...patch });

  return (
    <Box
      data-test-auto-recharge-box
      className={settings.isEnabled ? `${styles.optionBox} ${styles.optionBoxActive}` : styles.optionBox}
    >
      <Stack gap="200">
        <ToggleRow
          title="Auto-recharge"
          description="Recharge automatically so your agent never pauses."
          isChecked={settings.isEnabled}
          onChange={(isEnabled) => update({ isEnabled })}
        />
        {settings.isEnabled ? (
          <AutoRechargeRules settings={settings} errors={errors} rechargeCents={rechargeCents} onChange={onChange} />
        ) : null}
      </Stack>
    </Box>
  );
}

function AutoRechargeRules({ settings, errors, rechargeCents, onChange }: AutoRechargeBoxProps) {
  const update = (patch: Partial<AutoRechargeSettings>) => onChange({ ...settings, ...patch });

  return (
    <>
      <Stack gap="100">
        <FormLayoutGroup gap="150">
          <NumberField
            label="When usage reaches"
            value={settings.thresholdPercent ?? undefined}
            withControls={false}
            min={1}
            max={100}
            elementAfter={
              <Text color="secondary" pr="150">
                %
              </Text>
            }
            isInvalid={errors.threshold != null}
            onChange={(next) => update({ thresholdPercent: next ?? null })}
          />
          <DollarField
            label="Recharge for"
            value={rechargeCents / 100}
            isInvalid={false}
            isDisabled={true}
            onChange={() => undefined}
          />
        </FormLayoutGroup>
        <RuleLine settings={settings} errors={errors} rechargeCents={rechargeCents} />
      </Stack>
      <Divider className={styles.activeDivider} />
      <ToggleRow
        title="Monthly limit"
        description="Stop recharging once this much has been charged in a month."
        isChecked={settings.isMonthlyLimitEnabled}
        onChange={(isMonthlyLimitEnabled) =>
          update({ isMonthlyLimitEnabled, monthlyLimitDollars: settings.monthlyLimitDollars ?? rechargeCents / 100 })
        }
      />
      {settings.isMonthlyLimitEnabled ? (
        <MonthlyLimit
          limitDollars={settings.monthlyLimitDollars}
          error={errors.monthlyLimit}
          onChange={(monthlyLimitDollars) => update({ monthlyLimitDollars })}
        />
      ) : null}
    </>
  );
}

type RuleLineProps = {
  settings: AutoRechargeSettings;
  errors: AutoRechargeErrors;
  rechargeCents: number;
};

function RuleLine({ settings, errors, rechargeCents }: RuleLineProps) {
  if (errors.threshold != null) {
    return (
      <Text data-test-auto-recharge-error color="critical" size="sm">
        {errors.threshold}
      </Text>
    );
  }
  return (
    <Text data-test-auto-recharge-rule color="secondary" size="sm">
      When your agent usage reaches <Strong color="inherit">{settings.thresholdPercent}%</Strong>, we'll charge your
      card <Strong color="inherit">{formatCentsToUsd(rechargeCents)}</Strong> and recharge it back to 0%.
    </Text>
  );
}

type MonthlyLimitProps = {
  limitDollars: number | null;
  error: string | undefined;
  onChange: (limitDollars: number | null) => void;
};

function MonthlyLimit({ limitDollars, error, onChange }: MonthlyLimitProps) {
  return (
    <Stack gap="100">
      <DollarField
        label="Monthly limit amount"
        isLabelHidden={true}
        value={limitDollars}
        isInvalid={error != null}
        onChange={onChange}
      />
      {error == null ? null : (
        <Text color="critical" size="sm">
          {error}
        </Text>
      )}
    </Stack>
  );
}
